import type { MouseEvent, ReactNode } from "react"

import { useCitationFileDownload } from "@/hooks/use-citation-file"
import { downloadBlob } from "@/lib/download"

type SessionFileDownloadLinkProps = {
  children: ReactNode
  filename: string
  fileUrl: string
}

/** Authenticated inline download for a validated, registered session file. */
export function SessionFileDownloadLink({
  children,
  filename,
  fileUrl,
}: SessionFileDownloadLinkProps) {
  const downloadMutation = useCitationFileDownload()

  const handleDownload = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    try {
      const blob = await downloadMutation.mutateAsync(fileUrl)
      downloadBlob(blob, filename)
    } catch {
      // The shared mutation reports download failures. Consume the rejection so
      // a failed authenticated fetch never becomes an unhandled browser promise.
    }
  }

  return (
    <button
      aria-busy={downloadMutation.isPending}
      aria-label={`Download ${filename}`}
      className="text-primary font-inherit focus-visible:ring-ring inline cursor-pointer rounded-sm border-0 bg-transparent p-0 underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-wait disabled:opacity-70"
      disabled={downloadMutation.isPending}
      type="button"
      onClick={(event) => void handleDownload(event)}
    >
      {children}
    </button>
  )
}
